import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// IYANU v2 Monitor — moderate overnight LONG (filtered). Polls
// /strategies/api/strategies/iyanu/decisions/ every 20s. Celery-driven: evaluated
// 16:05 ET, reads OSUPA (session-scoped, DEFER+retry), exits 09:30 next day,
// -150bp stop. 5 gates: close>SMA100, slope>0, OSUPA skipped, pct_above<=400bp,
// ret5<-100bp. Independent book (ANYANWU-style, slot-exempt). cert 660E954F.

const REFRESH_MS = 20000;
const IYANU_COLOR = '#22c55e';   // green (overnight long)

const STATUS_COLORS = {
  OPEN: '#3b82f6', EXIT: '#3b82f6', STOP: T.bear, SKIP: T.textMuted, DEFER: T.gold, PAUSED: T.textMuted,
};

function fmtDate(s) { return s || '—'; }
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

// Time-of-day in ET from an ISO ts. Offset-less timestamps are treated as UTC so
// they don't drift by the browser tz.
function fmtET(ts) {
  if (!ts) return '';
  const s = /([+-]\d\d:?\d\d|Z)$/.test(ts) ? ts : ts + 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false });
}

function Stat({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 110px', padding: '12px 14px', borderRadius: 6,
      background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, color: color || T.text, fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>{value}</div>
    </div>
  );
}
function Pill({ value, color }) {
  if (value == null || value === '') return <span style={{ color: T.textMuted }}>—</span>;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10,
      fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: `${color || T.border}22`, border: `1px solid ${color || T.border}66`,
      color: color || T.text }}>{String(value)}</span>
  );
}

export default function IyanuMonitor() {
  const [data, setData] = useState({ decisions: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [limit, setLimit] = useState(100);
  const timerRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let url = `${API_BASE}/strategies/api/strategies/iyanu/decisions/?limit=${limit}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData({ decisions: j.decisions || [], summary: j.summary || {} });
      setLastFetched(new Date());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [limit, actionFilter]);

  useEffect(() => {
    fetchRows();
    timerRef.current = setInterval(fetchRows, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchRows]);

  const { decisions, summary } = data;

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1500, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: T.text }}>IYANỤ — Overnight LONG (NQ)</h1>
        <Pill value="v2 · PHASE 1" color={IYANU_COLOR} />
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16, lineHeight: 1.5 }}>
        Filtered overnight LONG · 16:05 ET eval (reads OSUPA, session-scoped retry) · gates: close&gt;SMA100, slope&gt;0,
        OSUPA-skipped, pct_above≤400bp, ret5&lt;−100bp · exit 09:30 · −150bp stop · independent book ·
        cert IYANU-LONG-LOCK-…660E954F · 44t / 80% WR / +44.7bp / DD −2.02% / PF 5.80×
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Evals" value={summary.evals ?? 0} />
        <Stat label="Takes" value={summary.takes ?? 0} color={IYANU_COLOR} />
        <Stat label="Skips" value={summary.skips ?? 0} color={T.textMuted} />
        <Stat label="Open" value={summary.open ?? 0} color="#3b82f6" />
        <Stat label="Net bp (take)" value={num(summary.net_bp, 1)}
          color={(summary.net_bp || 0) >= 0 ? T.bull : T.bear} />
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
          style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px' }}>
          <option value="">All actions</option>
          <option value="TAKE">TAKE</option>
          <option value="SKIP">SKIP</option>
          <option value="DEFER">DEFER</option>
        </select>
        <select value={limit} onChange={e => setLimit(Number(e.target.value))}
          style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px' }}>
          {[50, 100, 200].map(n => <option key={n} value={n}>{n} rows</option>)}
        </select>
        <button onClick={fetchRows} disabled={loading}
          style={{ background: IYANU_COLOR, color: '#000', border: 'none', borderRadius: 6, padding: '7px 16px', fontWeight: 700, cursor: 'pointer' }}>
          {loading ? '…' : 'Refresh'}</button>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          {decisions.length} rows · one eval/day 16:05 ET {lastFetched ? `· updated ${lastFetched.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false })} ET` : ''}
        </span>
      </div>

      {error && <div style={{ color: T.bear, marginBottom: 12 }}>Error: {error}</div>}

      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '60vh', border: `1px solid ${T.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.03)', textAlign: 'left' }}>
              {['Session', 'Decision', 'Close', 'SMA100', 'Slope bp', 'pct_above', 'ret5 bp', 'OSUPA', 'Stop', 'Status', 'Reason'].map(h => (
                <th key={h} style={{ padding: '10px 12px', color: T.textMuted, fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {decisions.length === 0 && (
              <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>No decisions yet.</td></tr>
            )}
            {decisions.map(r => (
              <tr key={r.id} style={{ borderTop: `1px solid ${T.border}` }}>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', whiteSpace: 'nowrap' }}>{fmtDate(r.session_date)}{r.signal_ts ? <span style={{ color: T.textMuted }}>{' ' + fmtET(r.signal_ts)}</span> : ''}</td>
                <td style={{ padding: '9px 12px' }}><Pill value={r.decision} color={r.action === 'TAKE' ? IYANU_COLOR : T.textMuted} /></td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{num(r.close)}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', color: T.textMuted }}>{num(r.sma100)}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', color: (r.slope_bp || 0) > 0 ? T.bull : T.bear }}>{num(r.slope_bp, 1)}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{num(r.pct_above, 0)}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', color: (r.ret5_bp || 0) < 0 ? T.bear : T.text }}>{num(r.ret5_bp, 0)}</td>
                <td style={{ padding: '9px 12px' }}>{r.osupa_took == null ? '—' : <Pill value={r.osupa_took ? 'TOOK' : 'SKIP'} color={r.osupa_took ? T.gold : T.textMuted} />}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', color: T.bear }}>{num(r.stop_price)}</td>
                <td style={{ padding: '9px 12px' }}><Pill value={r.status} color={STATUS_COLORS[r.status] || T.textMuted} /></td>
                <td style={{ padding: '9px 12px', color: T.textMuted, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
